<script setup lang="ts">
import otter from '@clovyr/bed/assets/images/otto-floating.svg';

import ClovyrLogoSmall from '@/components/icons/ClovyrLogoSmall.vue';

import 'line-awesome/dist/line-awesome/css/line-awesome.min.css';

defineProps<{
  title: string;
  description: string;
}>();
</script>

<template>
  <main class="local-sso-dialog">
    <section class="local-sso-dialog__surface" aria-labelledby="local-sso-dialog-title">
      <header class="local-sso-dialog__header">
        <div class="local-sso-dialog__brand" aria-label="Clovyr">
          <ClovyrLogoSmall class="local-sso-dialog__logo" aria-hidden="true" />
          <span>Clovyr</span>
        </div>
        <img class="local-sso-dialog__mascot" :src="otter" alt="" />
      </header>

      <div class="local-sso-dialog__content">
        <div class="local-sso-dialog__introduction">
          <h1 id="local-sso-dialog-title">{{ title }}</h1>
          <p>{{ description }}</p>
        </div>
        <slot />
      </div>

      <footer class="local-sso-dialog__footer" aria-label="Legal and support links">
        <RouterLink to="/privacy-policy">Privacy Policy</RouterLink>
        <a href="https://clovyr.io/terms-of-service" target="_blank" rel="noopener"
          >Terms of Service</a
        >
        <a href="https://clovyr.io/contact" target="_blank" rel="noopener">Help &amp; Support</a>
      </footer>
    </section>
  </main>
</template>

<style lang="scss" scoped>
.local-sso-dialog {
  --local-sso-page-padding: clamp(16px, 4vw, 48px);

  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100dvh;
  padding: var(--local-sso-page-padding);
  color: #251d3a;
  background-color: #211a33;
  background-size: 96px 96px;
  font-family: 'Work Sans', sans-serif;
}

.local-sso-dialog__surface {
  display: flex;
  flex-direction: column;
  width: min(100%, 46rem);
  overflow: hidden;
  background: #fff;
  border: 1px solid #dedde3;
  border-radius: 24px;
  box-shadow: 0 1.25rem 3.5rem rgb(11 6 24 / 18%);
}

.local-sso-dialog__header,
.local-sso-dialog__content {
  padding-right: clamp(24px, 6vw, 48px);
  padding-left: clamp(24px, 6vw, 48px);
}

.local-sso-dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: clamp(32px, 6vw, 48px);
}

.local-sso-dialog__brand {
  display: flex;
  gap: 16px;
  align-items: center;
  color: #251d3a;
  font-size: clamp(24px, 3.5vw, 28px);
  font-weight: 700;
  letter-spacing: -0.05em;
}

.local-sso-dialog__logo {
  width: clamp(40px, 5vw, 48px);
  height: clamp(40px, 5vw, 48px);
}

.local-sso-dialog__mascot {
  width: clamp(56px, 8vw, 72px);
  height: clamp(48px, 7vw, 60px);
  object-fit: contain;
}

.local-sso-dialog__content {
  width: 100%;
  padding-top: clamp(40px, 6vw, 48px);
  padding-bottom: 48px;
}

.local-sso-dialog__introduction h1 {
  max-width: 48rem;
  margin: 0;
  color: #251d3a;
  font-size: clamp(32px, 5vw, 40px);
  font-weight: 700;
  letter-spacing: -0.055em;
  line-height: 1.12;
}

.local-sso-dialog__introduction p {
  max-width: 48rem;
  margin: 16px 0 0;
  color: #83769c;
  font-size: clamp(18px, 3vw, 20px);
  font-weight: 400;
  letter-spacing: -0.025em;
  line-height: 1.65;
}

.local-sso-dialog__footer {
  display: flex;
  gap: clamp(16px, 4vw, 32px);
  align-items: center;
  justify-content: center;
  min-height: 72px;
  padding: 16px;
  margin-top: auto;
  border-top: 1px solid #dedde3;
}

.local-sso-dialog__footer a {
  color: #aaa3b7;
  font-size: clamp(14px, 2.4vw, 16px);
  font-weight: 500;
  letter-spacing: -0.025em;
  text-decoration: none;
}

.local-sso-dialog__footer a:hover,
.local-sso-dialog__footer a:focus-visible {
  color: #5cbd85;
  text-decoration: underline;
}

@media (width <= 40rem) {
  .local-sso-dialog {
    --local-sso-page-padding: 0;

    background-image: none;
  }

  .local-sso-dialog__surface {
    min-height: 100dvh;
    border: 0;
    border-radius: 0;
  }

  .local-sso-dialog__footer {
    flex-wrap: wrap;
    min-height: 0;
  }
}
</style>
